import { useAppStore } from "@/store";
import ProfilePicture from "@/components/ProfilePicture";
import { normalizeAgentName, getDisplayName, AgentId } from '@/constants/agentNames';
import { USER_AVATAR_GRADIENT_CLASS } from '@/utils/userUtils';

type MessageAvatarProps = {
  isUser: boolean,
  agentUsed?: string,
}

// Agent avatar configuration using canonical agent IDs
const AGENT_AVATAR_CONFIG: Record<string, { image: string; bgColor: string }> = {
  [AgentId.PLANT_SUPPORT]: { image: '/images/plant-support-agent.png', bgColor: '#FEF08A' },
  [AgentId.CHEMICAL]: { image: '/images/chemical-agent.png', bgColor: '#BFDBFE' },
  [AgentId.MECHANICAL]: { image: '/images/mechanical-agent.png', bgColor: '#BBF7D0' },
  [AgentId.ELECTRICAL]: { image: '/images/elec-agent.png', bgColor: '#E9D5FF' },
}

const DEFAULT_AVATAR = AGENT_AVATAR_CONFIG[AgentId.PLANT_SUPPORT]

const MessageAvatar = ({ isUser, agentUsed }: MessageAvatarProps) => {
  const { user } = useAppStore();

  // Get agent avatar using normalized name
  const getAgentAvatar = (agent?: string) => {
    if (!agent) return { ...DEFAULT_AVATAR, name: getDisplayName(AgentId.PLANT_SUPPORT) }
    const normalizedAgent = normalizeAgentName(agent)
    const config = AGENT_AVATAR_CONFIG[normalizedAgent] || DEFAULT_AVATAR
    return { ...config, name: getDisplayName(normalizedAgent) }
  }

  const agentAvatar = getAgentAvatar(agentUsed)

  return (
    <div className="flex flex-col rounded-full">
      {isUser ? (
        // ProfilePicture paints the shared avatar gradient itself (FS-581), so
        // the green ring this used to wrap it in is gone — it framed the
        // gradient in a second colour and disagreed with every other surface.
        user ? (
          <ProfilePicture user={user} size="40" clickable={false} />
        ) : (
          <div
            className={`flex size-10 items-center justify-center rounded-full text-xs font-semibold text-white ${USER_AVATAR_GRADIENT_CLASS}`}
          >
            ME
          </div>
        )
      ) : (
        <div className="w-10 rounded-full shadow-md flex items-center justify-center overflow-hidden" style={{ backgroundColor: agentAvatar.bgColor }}>
          <img
            src={agentAvatar.image}
            alt={agentAvatar.name}
            className="w-10 object-cover rounded-full"
            draggable={false}
          />
        </div>
      )}
    </div>
  )
}

export default MessageAvatar;